import { useClient } from 'urql';
import { schoolPageBySlugTabContentQuery } from '../../../../../graphql/schoolPageBySlugTabContentQuery';
import {
  useSchoolPageBySlugTabContentQuery,
  useSchoolTabContentQueryVariables,
} from '../../../../../graphql/useSchoolPageBySlugTabContentQuery';
import { useEffect, useMemo, useState } from 'react';
import { PropertyCollectionProperty } from '../../../../../types';

export const usePaginatedSchoolPageBySlugTabContentQuery = () => {
  const client = useClient();
  const vars = useSchoolTabContentQueryVariables();
  const [nextPage, setNextPage] = useState<number | null>(null);
  const [properties, setProperties] = useState<PropertyCollectionProperty[] | null>(null);

  const [loadMoreFetching, setLoadMoreFetching] = useState(false);

  const { data: initialData, fetching: initialFetching } = useSchoolPageBySlugTabContentQuery();

  const initialTabContent =
    !initialData?.schoolPageBySlug?.tabContent ||
    !('propertyCollection' in initialData.schoolPageBySlug.tabContent)
      ? null
      : initialData.schoolPageBySlug.tabContent;
  const initialResults = useMemo(
    () => initialTabContent?.propertyCollection?.properties?.nodes || [],
    [initialTabContent],
  );
  const initialNextPage =
    initialTabContent?.propertyCollection?.properties?.pageInfo.nextPage || null;

  useEffect(() => {
    setNextPage(initialNextPage);
    setProperties(initialResults);
  }, [initialResults, initialNextPage]);

  const fetchMore = async () => {
    if (!nextPage || !properties) return;

    setLoadMoreFetching(true);

    const { data: showMoreData } = await client.query(
      schoolPageBySlugTabContentQuery,
      {
        ...vars,
        page: nextPage,
      },
      { requestPolicy: 'network-only' },
    );

    const showMoreTabContent =
      !showMoreData?.schoolPageBySlug?.tabContent ||
      !('propertyCollection' in showMoreData.schoolPageBySlug.tabContent)
        ? null
        : showMoreData.schoolPageBySlug.tabContent;

    const showMoreResults = showMoreTabContent?.propertyCollection?.properties?.nodes || [];
    const showMoreNextPage =
      showMoreTabContent?.propertyCollection?.properties?.pageInfo.nextPage || null;

    setProperties(properties.concat(showMoreResults));
    setNextPage(showMoreNextPage);

    setLoadMoreFetching(false);
  };

  return {
    properties: properties || initialResults,
    fetching: loadMoreFetching || initialFetching,
    fetchMore,
  };
};
